import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  IndianRupee,
  Percent,
  Users,
  Search,
  Filter,
  Download,
  Eye,
  TrendingUp,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Calendar,
  AlertTriangle,
  CheckCircle,
} from "lucide-react";

const COMMISSION_TYPES = [
  { id: "all", name: "All Types" },
  { id: "percentage", name: "Percentage" },
  { id: "flat_per_ride", name: "Flat per Ride" },
  { id: "fixed_daily", name: "Fixed Daily" },
  { id: "fixed_weekly", name: "Fixed Weekly" },
  { id: "fixed_monthly", name: "Fixed Monthly" },
];

export const CityAdminCommissionReport = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  
  // Separated reports
  const [perRideReport, setPerRideReport] = useState(null);
  const [subscriptionReport, setSubscriptionReport] = useState(null);
  const [reportView, setReportView] = useState("combined");
  
  const [commissionTypeFilter, setCommissionTypeFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [hasDuesFilter, setHasDuesFilter] = useState("all");
  
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [driverReport, setDriverReport] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [summaryExpanded, setSummaryExpanded] = useState(true);

  useEffect(() => {
    if (user?.id) fetchData();
  }, [user?.id, commissionTypeFilter, hasDuesFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (commissionTypeFilter && commissionTypeFilter !== "all") params.append("commission_type", commissionTypeFilter);
      if (hasDuesFilter === "yes") params.append("has_dues", "true");
      if (hasDuesFilter === "no") params.append("has_dues", "false");
      
      const [res, perRideRes, subRes] = await Promise.all([
        axios.get(`${API}/city-admin/${user.id}/commission-report?${params.toString()}`),
        axios.get(`${API}/commission-reports/admin/${user.id}/per-ride`).catch(() => ({ data: null })),
        axios.get(`${API}/commission-reports/admin/${user.id}/subscription`).catch(() => ({ data: null }))
      ]);
      setReport(res.data);
      setPerRideReport(perRideRes.data);
      setSubscriptionReport(subRes.data);
    } catch (err) {
      toast.error("Failed to load commission report");
    } finally {
      setLoading(false);
    }
  };

  const fetchDriverDetail = async (driverId) => {
    try {
      const res = await axios.get(`${API}/city-admin/${user.id}/driver/${driverId}/full-report`);
      setDriverReport(res.data);
      setShowDetailDialog(true);
    } catch (err) {
      toast.error("Failed to load driver details");
    }
  };

  const filteredDrivers = (report?.drivers || []).filter(d => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return d.driver_name?.toLowerCase().includes(search) || d.driver_phone?.includes(search);
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric"
    });
  };

  const exportReport = () => {
    const csv = [
      ["Driver Name", "Phone", "Commission Type", "Total Rides", "Earnings", "Tips", "Commission", "Paid", "Dues"].join(","),
      ...filteredDrivers.map(d => [
        d.driver_name, d.driver_phone, d.commission_type, d.total_rides,
        d.total_earnings, d.total_tips, d.total_commission, d.total_paid, d.amount_owed
      ].join(","))
    ].join("\n");
    
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `commission-report-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="city-admin-commission-report">
      {/* Summary Cards */}
      <div className="bg-card border rounded-lg p-4">
        <div className="flex items-center justify-between cursor-pointer" onClick={() => setSummaryExpanded(!summaryExpanded)}>
          <h3 className="font-semibold flex items-center gap-2">
            <TrendingUp className="w-5 h-5" /> Summary Overview
          </h3>
          {summaryExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </div>
        
        {summaryExpanded && report?.summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mt-4">
            <div className="bg-blue-50 p-3 rounded-lg">
              <p className="text-sm text-blue-600">Total Drivers</p>
              <p className="text-2xl font-bold text-blue-700">{report.summary.total_drivers}</p>
            </div>
            <div className="bg-green-50 p-3 rounded-lg">
              <p className="text-sm text-green-600">Total Earnings</p>
              <p className="text-2xl font-bold text-green-700">₹{report.summary.total_earnings?.toLocaleString()}</p>
            </div>
            <div className="bg-yellow-50 p-3 rounded-lg">
              <p className="text-sm text-yellow-600">Total Tips</p>
              <p className="text-2xl font-bold text-yellow-700">₹{report.summary.total_tips?.toLocaleString()}</p>
            </div>
            <div className="bg-purple-50 p-3 rounded-lg">
              <p className="text-sm text-purple-600">Total Commission</p>
              <p className="text-2xl font-bold text-purple-700">₹{report.summary.total_commission?.toLocaleString()}</p>
            </div>
            <div className="bg-teal-50 p-3 rounded-lg">
              <p className="text-sm text-teal-600">Total Paid</p>
              <p className="text-2xl font-bold text-teal-700">₹{report.summary.total_paid?.toLocaleString()}</p>
            </div>
            <div className="bg-red-50 p-3 rounded-lg">
              <p className="text-sm text-red-600">Total Owed</p>
              <p className="text-2xl font-bold text-red-700">₹{report.summary.total_owed?.toLocaleString()}</p>
            </div>
          </div>
        )}
      </div>

      {/* Report View Toggle */}
      <div className="flex gap-2 flex-wrap">
        <Button 
          variant={reportView === "combined" ? "default" : "outline"}
          onClick={() => setReportView("combined")}
          className="flex gap-2"
        >
          <Users className="w-4 h-4" />
          Combined
        </Button>
        <Button 
          variant={reportView === "per-ride" ? "default" : "outline"}
          onClick={() => setReportView("per-ride")}
          className="flex gap-2"
        >
          <Percent className="w-4 h-4" />
          Per-Ride ({perRideReport?.summary?.total_drivers || 0})
        </Button>
        <Button 
          variant={reportView === "subscription" ? "default" : "outline"}
          onClick={() => setReportView("subscription")}
          className="flex gap-2"
        >
          <Calendar className="w-4 h-4" />
          Subscription ({subscriptionReport?.summary?.total_drivers || 0})
        </Button>
      </div>

      {/* Subscription Report View */}
      {reportView === "subscription" && subscriptionReport && (
        <div className="bg-card border rounded-lg overflow-hidden">
          <div className="p-4 border-b">
            <h3 className="font-semibold">Subscription Drivers</h3>
            <div className="flex gap-4 mt-2 text-sm">
              <span>Daily: <strong>{subscriptionReport.summary?.daily_drivers || 0}</strong></span>
              <span>Weekly: <strong>{subscriptionReport.summary?.weekly_drivers || 0}</strong></span>
              <span>Monthly: <strong>{subscriptionReport.summary?.monthly_drivers || 0}</strong></span>
              <span className="text-amber-600">Total Dues: <strong>₹{subscriptionReport.summary?.total_due_amount?.toLocaleString() || 0}</strong></span>
            </div>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Driver</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Due</TableHead>
                <TableHead>Next Renewal</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {subscriptionReport.drivers?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    No subscription drivers found
                  </TableCell>
                </TableRow>
              ) : (
                subscriptionReport.drivers?.map((driver) => (
                  <TableRow key={driver.id} className={driver.is_renewal_overdue ? "bg-red-50" : ""}>
                    <TableCell>
                      <p className="font-medium">{driver.name}</p>
                      <p className="text-xs text-muted-foreground">{driver.phone}</p>
                    </TableCell>
                    <TableCell>{driver.plan_name}</TableCell>
                    <TableCell>₹{driver.subscription_amount}</TableCell>
                    <TableCell className={driver.due_amount > 0 ? "text-amber-600 font-medium" : ""}>
                      ₹{driver.due_amount}
                    </TableCell>
                    <TableCell className={driver.is_renewal_overdue ? "text-red-600" : ""}>
                      {formatDate(driver.next_renewal_date)}
                    </TableCell>
                    <TableCell>
                      {driver.is_renewal_overdue ? (
                        <span className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded flex items-center gap-1 w-fit">
                          <AlertTriangle className="w-3 h-3" /> Overdue
                        </span>
                      ) : (
                        <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded flex items-center gap-1 w-fit">
                          <CheckCircle className="w-3 h-3" /> Active
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Per-Ride Report View */}
      {reportView === "per-ride" && perRideReport && (
        <div className="bg-card border rounded-lg overflow-hidden">
          <div className="p-4 border-b">
            <h3 className="font-semibold">Per-Ride Drivers</h3>
            <div className="flex gap-4 mt-2 text-sm">
              <span>Total: <strong>{perRideReport.summary?.total_drivers || 0}</strong></span>
              <span className="text-green-600">Collected: <strong>₹{perRideReport.summary?.total_collected?.toLocaleString() || 0}</strong></span>
              <span className="text-amber-600">Pending: <strong>₹{perRideReport.summary?.total_pending?.toLocaleString() || 0}</strong></span>
            </div>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Driver</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Rides</TableHead>
                <TableHead>Total Fare</TableHead>
                <TableHead>Commission</TableHead>
                <TableHead>Paid</TableHead>
                <TableHead>Pending</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {perRideReport.drivers?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    No per-ride drivers found
                  </TableCell>
                </TableRow>
              ) : (
                perRideReport.drivers?.map((driver) => (
                  <TableRow key={driver.id}>
                    <TableCell>
                      <p className="font-medium">{driver.name}</p>
                      <p className="text-xs text-muted-foreground">{driver.phone}</p>
                    </TableCell>
                    <TableCell>
                      {driver.commission_type === "percentage" ? `${driver.commission_rate}%` : `₹${driver.commission_rate}/ride`}
                    </TableCell>
                    <TableCell>{driver.total_rides}</TableCell>
                    <TableCell>₹{driver.total_fare?.toLocaleString()}</TableCell>
                    <TableCell>₹{driver.total_commission?.toLocaleString()}</TableCell>
                    <TableCell className="text-green-600">₹{driver.total_paid?.toLocaleString()}</TableCell>
                    <TableCell className={driver.pending_amount > 0 ? "text-amber-600 font-medium" : ""}>
                      ₹{driver.pending_amount?.toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Combined View - Original Filters and Table */}
      {reportView === "combined" && (
        <>
      {/* Filters */}
      <div className="bg-card border rounded-lg p-4">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-muted-foreground" />
          <h3 className="font-semibold">Filters</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <Label className="text-sm">Commission Type</Label>
            <Select value={commissionTypeFilter} onValueChange={setCommissionTypeFilter}>
              <SelectTrigger><SelectValue placeholder="All Types" /></SelectTrigger>
              <SelectContent>
                {COMMISSION_TYPES.map((type) => (
                  <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm">Has Dues</Label>
            <Select value={hasDuesFilter} onValueChange={setHasDuesFilter}>
              <SelectTrigger><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="yes">With Dues</SelectItem>
                <SelectItem value="no">No Dues</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm">Search</Label>
            <div className="relative">
              <Search className="absolute left-2 top-2.5 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Name or phone..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-8" />
            </div>
          </div>
          <div className="flex items-end gap-2">
            <Button variant="outline" onClick={fetchData}><RefreshCw className="w-4 h-4 mr-2" /> Refresh</Button>
            <Button variant="outline" onClick={exportReport}><Download className="w-4 h-4 mr-2" /> Export</Button>
          </div>
        </div>
      </div>

      {/* Drivers Table */}
      <div className="bg-card border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Driver</TableHead>
              <TableHead>Commission Type</TableHead>
              <TableHead className="text-right">Rides</TableHead>
              <TableHead className="text-right">Earnings</TableHead>
              <TableHead className="text-right">Commission</TableHead>
              <TableHead className="text-right">Paid</TableHead>
              <TableHead className="text-right">Dues</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredDrivers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No drivers found</TableCell>
              </TableRow>
            ) : (
              filteredDrivers.map((driver) => (
                <TableRow key={driver.driver_id}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{driver.driver_name}</p>
                      <p className="text-sm text-muted-foreground">{driver.driver_phone}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      driver.commission_type === "percentage" ? "bg-blue-100 text-blue-700" :
                      driver.commission_type === "flat_per_ride" ? "bg-green-100 text-green-700" :
                      "bg-purple-100 text-purple-700"
                    }`}>
                      {driver.commission_type?.replace("_", " ")}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">{driver.total_rides}</TableCell>
                  <TableCell className="text-right">₹{driver.total_earnings?.toLocaleString()}</TableCell>
                  <TableCell className="text-right">₹{driver.total_commission?.toLocaleString()}</TableCell>
                  <TableCell className="text-right text-green-600">₹{driver.total_paid?.toLocaleString()}</TableCell>
                  <TableCell className="text-right">
                    <span className={driver.amount_owed > 0 ? "text-red-600 font-semibold" : "text-green-600"}>
                      ₹{driver.amount_owed?.toLocaleString()}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Button size="sm" variant="ghost" onClick={() => fetchDriverDetail(driver.driver_id)}>
                      <Eye className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      </>
      )}

      {/* Driver Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Driver Report: {driverReport?.driver_name}</DialogTitle>
          </DialogHeader>
          {driverReport && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 p-3 rounded">
                  <p className="text-sm text-blue-600">Total Rides</p>
                  <p className="text-xl font-bold text-blue-700">{driverReport.summary?.total_rides}</p>
                </div>
                <div className="bg-green-50 p-3 rounded">
                  <p className="text-sm text-green-600">Earnings</p>
                  <p className="text-xl font-bold text-green-700">₹{driverReport.summary?.total_earnings?.toLocaleString()}</p>
                </div>
                <div className="bg-purple-50 p-3 rounded">
                  <p className="text-sm text-purple-600">Commission</p>
                  <p className="text-xl font-bold text-purple-700">₹{driverReport.commission_summary?.total_commission?.toLocaleString()}</p>
                </div>
                <div className="bg-red-50 p-3 rounded">
                  <p className="text-sm text-red-600">Dues</p>
                  <p className="text-xl font-bold text-red-700">₹{driverReport.commission_summary?.amount_owed?.toLocaleString()}</p>
                </div>
              </div>
              
              {driverReport.breakdown?.by_booking_type && Object.keys(driverReport.breakdown.by_booking_type).length > 0 && (
                <div className="border rounded p-4">
                  <h4 className="font-semibold mb-3">By Booking Type</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {Object.entries(driverReport.breakdown.by_booking_type).map(([type, data]) => (
                      <div key={type} className="bg-gray-50 p-2 rounded">
                        <p className="text-sm font-medium capitalize">{type.replace("_", " ")}</p>
                        <p className="text-xs text-muted-foreground">{data.rides} rides • ₹{data.earnings}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CityAdminCommissionReport;
